<template>
	<SuiModalDialog
		class="resource-booking-modal"
		data-testid="resource-booking-modal"
	>
		<template #header>
			<h1>
				{{
					bookingId
						? $t('resource-booking__modify-booking')
						: $t('resource-booking__new-booking')
				}}
			</h1>
		</template>

		<div class="resource-booking-modal__form">
			<ResourcePlanningUserSelectDropdown
				class="resource-booking-modal__user-select"
				:is-user-selection-enabled="Boolean(bookingId)"
				:selected-entity-id="booking.selectedEntityId"
				:selected-entity-type="booking.selectedEntityType"
				:selected-entity-name="selectedEntityName"
				@user-selected="onUserSelected"
			/>
			<div class="resource-booking-modal__form-content">
				<div v-if="showProject" class="resource-booking-modal__project">
					<SuiIcon name="icon-briefcase-16" />
					{{ projectName }}
				</div>
				<div class="resource-booking-modal__inputs-wrapper">
					<div class="resource-booking-modal__inputs-column">
						<div
							class="resource-booking-modal__date-select-wrapper"
						>
							<AppDatePicker
								:key="startDatePickerKey"
								v-tooltip="dateDisabledTooltip"
								:model-value="booking.startDate"
								data-testid="booking-start-date-input"
								class="resource-booking-modal__start-date-input"
								:format="dateFormat"
								append-to-body
								:disabled="isStartEndDateDisabled"
								:date-disabled="isStartDateDisabled"
								:label="t('resource-booking__timeframe')"
								:variant="
									v.startDate.$error ? 'error' : undefined
								"
								@update:model-value="onUpdateStartDate"
							/>
							<AppDatePicker
								:key="endDatePickerKey"
								v-tooltip="dateDisabledTooltip"
								:model-value="booking.endDate"
								data-testid="booking-end-date-input"
								class="resource-booking-modal__end-date-input"
								:date-disabled="isEndDateDisabled"
								:disabled="isStartEndDateDisabled"
								:format="dateFormat"
								append-to-body
								:variant="
									v.endDate.$error ? 'error' : undefined
								"
								@update:model-value="onUpdateEndDate"
							/>
						</div>
						<div
							v-tooltip="totalWorkingDaysTooltip"
							class="resource-booking-modal__working-days"
						>
							{{ $t('resource_booking_working_days') }}:
							<span
								class="resource-booking-modal__working-days-text"
							>
								{{ workingDays }}
							</span>
						</div>
					</div>

					<div class="resource-booking-modal__inputs-column">
						<div
							class="resource-booking-modal__amount-select-wrapper"
						>
							<SuiNumberInput
								v-if="
									booking.unit === BookingAmountType.Percent
								"
								v-model="booking.amountPercent"
								:label="t('resource-booking__amount')"
								class="resource-booking-modal__amount-input"
								:variant="
									v.amountPercent.$error ? 'error' : undefined
								"
								@blur="validateInputField('amountPercent')"
							/>
							<SuiTimeInput
								v-else
								v-model="booking.amountTime"
								:label="t('resource-booking__amount')"
								class="resource-booking-modal__amount-input"
								:variant="
									v.amountTime.$error ? 'error' : undefined
								"
								:hours-per-day="hoursPerDay"
								@blur="validateInputField('amountTime')"
							/>
							<SuiSelect
								:model-value="booking.unit"
								:aria-label="t('resource-booking__unit')"
								hide-clear-button
								:options="unitSelectOptions"
								class="resource-booking-modal__amount-type"
								@update:model-value="onUpdateBookingUnit"
							>
								<template #no-results-content>
									{{ $t('search.noResults') }}
								</template>
							</SuiSelect>
						</div>
						<div
							v-tooltip="totalHoursTooltip"
							class="resource-booking-modal__total"
						>
							{{ resourceBookingTotalText }}
							<span class="resource-booking-modal__total-text">
								{{ bookingDurationToDisplay }}
							</span>
						</div>
					</div>
				</div>

				<SuiInput
					v-model="booking.description"
					:label="$t('resource-booking__description')"
					:variant="v.description.$error ? 'error' : undefined"
					class="resource-booking-modal__description"
					@blur="validateInputField('description')"
				/>

				<ResourcePlanningBookingModalAllocationTypeSelection
					v-if="hasTentativeBookingsFeature"
					v-model="selectedAllocationType"
					:is-fixed-booking-disabled="isFixedBookingDisabled"
				/>
			</div>
		</div>

		<template #footer>
			<SuiTextButton
				v-if="bookingId"
				v-tooltip="$t('resource-booking__delete')"
				:aria-label="$t('resource-booking__delete')"
				class="resource-booking-modal__delete-button"
				:disabled="loadingDelete"
				@click="onDeleteBooking"
			>
				<SuiIcon name="icon-bin-16" />
			</SuiTextButton>

			<div class="resource-booking-modal__button-wrapper">
				<SuiButton variant="secondary-outlined" @click="modalClose">
					{{ $t('cancel') }}
				</SuiButton>
				<SuiButton
					v-tooltip="saveButtonDisabledTooltip"
					variant="primary"
					:is-loading="isLoadingSaveBooking"
					:disabled="isSaveButtonDisabled"
					@click="onResourceSave"
				>
					{{ $t('save') }}
				</SuiButton>
			</div>
		</template>
	</SuiModalDialog>
</template>
<script setup lang="ts">
import {
	ref,
	reactive,
	computed,
	toRefs,
	onMounted,
	onUnmounted,
	watch,
} from 'vue';
import {
	SuiSelect,
	SuiButton,
	SuiTimeInput,
	SuiNumberInput,
	SuiIcon,
	SuiTextButton,
	SuiInput,
	SuiModalDialog,
} from '@developers/scoro-ui';
import { AppDatePicker } from '@/features/App';
import { useFormatter, useTranslation } from '@/composables';
import {
	DEFAULT_BOOKING_AMOUNT,
	ZOOM_OPTIONS,
	BOOKING_ALLOCATION_TYPE,
} from '../../constants';
import {
	closePopOver,
	getRemainingAvailabilityDuration,
	getTotalBookingDuration,
	weekStartsOnSettingDateFnsMapping,
} from '../ResourcePlanningGantt/ResourcePlanningGanttUtils';
import { ResourcePlanningUserSelectDropdown } from '../ResourcePlanningUserSelect';
import {
	lastDayOfWeek,
	lastDayOfMonth,
	isBefore,
	addYears,
	isAfter,
	subYears,
	isWithinInterval,
} from 'date-fns';
import { resetTime, shortDuration } from '@/utils/formatting';
import { timeToSeconds } from '@/utils/time';
import {
	useBooleanSetting,
	useSettings,
} from '@/composables/settings/useSettings';
import { Setting, WEEK_STARTS_ON } from '@/constants/settings';
import {
	useResourceBookingDeleteMutation,
	BookingAmountType,
	type User,
	type RoleProject,
	type Booking,
	type BookingSaveInput,
	type Project,
	TYPES,
	type Role,
} from '@/generated/graphql';
import { useMessage } from '@/features/Message';
import { useVuelidate } from '@vuelidate/core';
import { maxLength } from '@vuelidate/validators';
import {
	largerThanZeroTime,
	largerThanZeroNumber,
	required,
} from '@/utils/validators';
import { useAvailabilityService } from '@/features/Availability';
import { type ZoomLevelName, type BookingAllocationType } from '../../types';
import ResourcePlanningBookingModalAllocationTypeSelection from './ResourcePlanningBookingModalAllocationTypeSelection.vue';
import { track } from '@/lib/amplitude';
import { Module } from '@/constants/modules';
import { useResourcePlanningUtils } from '@/features/ResourcePlanning/components/ResourcePlanningGantt/useResourcePlanningUtils';
import {
	BOOKING_DATE_YEARS_FORWARD,
	BOOKING_DATE_YEARS_BACK,
	BOOKING_DATE_RANGE_YEARS,
} from './constants';
import { type SAPIDate } from '@/types/sapi/scalars';

type BookingForm = {
	bookingId: string | undefined;
	selectedEntityId: User['id'] | RoleProject['id'];
	selectedEntityType: typeof TYPES.User | typeof TYPES.RoleProject;
	startDate: SAPIDate;
	endDate: SAPIDate;
	amountTime: string;
	amountPercent: string;
	unit: BookingAmountType;
	hasSaveAttempt: boolean;
	isTentative: boolean;
	projectId: Project['id'];
	description: string;
};

// If the startDate passed we make the booking according to that date and gantt zoom level
// else we make the booking adjusted by projectStartDate
export type ResourcePlanningBookingModalProps = {
	projectId: Project['id'];
	startDate?: Date;
	endDate?: Date;
	zoomLevel?: ZoomLevelName;
	projectStartDate?: Date;
	projectName?: Project['name'];
	bookingId?: Booking['id'];
	selectedEntityId: User['id'] | RoleProject['id'];
	selectedEntityType: typeof TYPES.User | typeof TYPES.RoleProject;
	selectedEntityName?: Role['name'];
	amount?: Booking['amount'];
	amountType?: BookingAmountType;
	isTentative?: boolean;
	hasTentativeBookingsFeature?: boolean;
	showProject?: boolean;
	description?: Booking['description'];
};

const dateToday = resetTime(new Date());
const dateAllowedMin = subYears(dateToday, BOOKING_DATE_YEARS_BACK);
const dateAllowedMax = addYears(dateToday, BOOKING_DATE_YEARS_FORWARD);

const props = defineProps<ResourcePlanningBookingModalProps>();

const {
	bookingId,
	projectId,
	selectedEntityId,
	selectedEntityType,
	startDate,
	endDate,
	amountType,
	amount,
	isTentative,
	description,
} = toRefs(props);

const emit = defineEmits<{
	(
		event: 'update-booking',
		input: BookingSaveInput,
		oldEntityId: User['id'] | RoleProject['id'],
		oldEntityType: typeof TYPES.RoleProject | typeof TYPES.User
	): void;
}>();

const {
	dateFormatForSUIdatePicker: dateFormat,
	formatLongDuration,
	dateToMysqlString,
} = useFormatter();
const { t } = useTranslation();
const { setting } = useSettings();
const { loadAvailability } = useAvailabilityService();
const { getUserAvailability } = useAvailabilityService();
const { addErrorMessage } = useMessage();

const weekStartsOnSetting = computed(() =>
	Number(setting(Setting.WEEK_STARTS_ON) ?? WEEK_STARTS_ON.MONDAY)
);

const [includeWeekends] = useBooleanSetting(
	Setting.INCLUDE_WEEKENDS_WHEN_SCHEDULING
);

const hoursPerDay = computed(() =>
	Number(setting(Setting.SYSTEM_DAY_UNIT_SUB_VALUE))
);

const bookingStartDate = computed(() => {
	if (!props.startDate && props.projectStartDate) {
		const projectStartDate = resetTime(props.projectStartDate);
		return resetTime(projectStartDate) <= resetTime(dateToday)
			? dateToday
			: projectStartDate;
	}
	return props.startDate ? resetTime(props.startDate) : dateToday;
});

const bookingEndDate = computed(() => {
	if (endDate?.value) {
		return endDate.value;
	}

	let tmpEndDate = resetTime(bookingStartDate.value);

	if (!startDate?.value) {
		tmpEndDate.setDate(tmpEndDate.getDate() + 6);
		return tmpEndDate;
	} else if (props.zoomLevel === ZOOM_OPTIONS.WEEK) {
		const weekStartsOn =
			weekStartsOnSettingDateFnsMapping[weekStartsOnSetting.value];

		tmpEndDate = lastDayOfWeek(bookingStartDate.value, { weekStartsOn });
	} else if (props.zoomLevel === ZOOM_OPTIONS.MONTH) {
		tmpEndDate = lastDayOfMonth(bookingStartDate.value);
	}

	return tmpEndDate;
});

const getFormattedDefaultBookingAmountPerDay = () =>
	shortDuration(DEFAULT_BOOKING_AMOUNT.TIME_PER_DAY)[0].value;

const isBookingTypePercent = computed(
	() => amountType?.value === BookingAmountType.Percent
);

const amountTime = computed(() => {
	if (!isBookingTypePercent.value && amount?.value) {
		return shortDuration(amount.value)[0].value;
	}

	return getFormattedDefaultBookingAmountPerDay();
});

const percentAmount = computed(() => {
	if (amount?.value && isBookingTypePercent.value) {
		return amount?.value;
	}

	return DEFAULT_BOOKING_AMOUNT.PERCENT;
});

const getBookingInitialValues = (): BookingForm => {
	return {
		bookingId: bookingId?.value,
		selectedEntityId: selectedEntityId.value,
		selectedEntityType: selectedEntityType.value,
		startDate: dateToMysqlString(bookingStartDate.value),
		endDate: dateToMysqlString(bookingEndDate.value),
		amountTime: amountTime.value,
		amountPercent: String(percentAmount.value),
		unit: amountType?.value ?? BookingAmountType.Hour,
		hasSaveAttempt: false,
		isTentative: isTentative.value,
		projectId: projectId.value,
		description: description?.value ?? '',
	};
};

const booking = reactive<BookingForm>({ ...getBookingInitialValues() });

const selectedAllocationType = computed<BookingAllocationType>({
	get: () =>
		booking.isTentative
			? BOOKING_ALLOCATION_TYPE.TENTATIVE
			: BOOKING_ALLOCATION_TYPE.FIXED,
	set: (value) => {
		booking.isTentative = value === BOOKING_ALLOCATION_TYPE.TENTATIVE;
	},
});

const isFixedBookingDisabled = computed(
	() => booking.selectedEntityType === TYPES.RoleProject
);

const isStartEndDateDisabled = computed(() => {
	if (!bookingStartDate.value) {
		return false;
	}
	return isBefore(bookingStartDate.value, dateAllowedMin);
});

const dateDisabledTooltip = computed(() => {
	if (!isStartEndDateDisabled.value) {
		return null;
	}
	return t('resource_booking_modal_date_before_allowed');
});

const isDateWithinAllowedMinMaxDates = (date: Date): boolean => {
	return !isBefore(date, dateAllowedMin) && !isAfter(date, dateAllowedMax);
};

const isStartDateValid = (date: SAPIDate): boolean => {
	const startDate = resetTime(date);
	return isDateWithinAllowedMinMaxDates(startDate);
};

const isEndDateValid = (date: SAPIDate): boolean => {
	const endDate = resetTime(date);
	const startDate = resetTime(booking.startDate);

	if (!isDateWithinAllowedMinMaxDates(endDate)) {
		return false;
	}

	return endDate >= startDate;
};

const isStartDateDisabled = (date: SAPIDate): boolean => {
	return !isStartDateValid(date);
};

const isEndDateDisabled = (date: SAPIDate): boolean => {
	return !isEndDateValid(date);
};

const validateStartDate = (date: SAPIDate): boolean => {
	const startDate = resetTime(date);
	const endDate = resetTime(booking.endDate);

	if (!isDateWithinAllowedMinMaxDates(startDate) || startDate > endDate) {
		return false;
	}

	return isWithinInterval(startDate, {
		start: subYears(endDate, BOOKING_DATE_RANGE_YEARS),
		end: addYears(endDate, BOOKING_DATE_RANGE_YEARS),
	});
};

const validateEndDate = (date: SAPIDate): boolean => {
	const endDate = resetTime(date);
	const startDate = resetTime(booking.startDate);

	if (!isDateWithinAllowedMinMaxDates(endDate)) {
		return false;
	}

	if (endDate < startDate) {
		return false;
	}

	return isWithinInterval(endDate, {
		start: subYears(startDate, BOOKING_DATE_RANGE_YEARS),
		end: addYears(startDate, BOOKING_DATE_RANGE_YEARS),
	});
};

const rules = computed(() => {
	const rules = {
		startDate: { validateStartDate },
		endDate: { validateEndDate },
		amountPercent: {},
		amountTime: {},
		description: { maxLength: maxLength(255) },
	};

	if (booking.unit === BookingAmountType.Percent) {
		rules.amountPercent = { required, largerThanZeroNumber };
	} else {
		rules.amountTime = { largerThanZeroTime };
	}

	return rules;
});

const v = useVuelidate(rules, booking, { $rewardEarly: true });

const validateInputField = (field: string) => {
	v.value[field].$touch();
	v.value[field].$commit();
};

watch(
	() => booking.unit,
	(val: BookingAmountType) => {
		if (val === BookingAmountType.Percent) {
			validateInputField('amountPercent');
		} else {
			validateInputField('amountTime');
		}
	}
);

const resourceBookingTotalText = computed(() =>
	booking.unit === BookingAmountType.Total
		? t('resource-booking__per-day-duration')
		: t('resource-booking__total-duration')
);

const totalWorkingDaysTooltip = computed(() => {
	if (selectedEntityType.value === TYPES.User) {
		return t('resource-booking__total-workdays-individual-tooltip');
	}

	if (selectedEntityType.value === TYPES.RoleProject) {
		return t('resource-booking__total-workdays-placeholder-tooltip');
	}

	return '';
});

const totalHoursTooltip = computed(() => {
	if (booking.unit === BookingAmountType.Percent) {
		if (selectedEntityType.value === TYPES.User) {
			return t('resource-booking__total-hours-individual-tooltip');
		}

		if (selectedEntityType.value === TYPES.RoleProject) {
			return t('resource-booking__total-hours-placeholder-tooltip');
		}
	}

	return '';
});

const modalClose = () => {
	Object.assign(booking, { ...getBookingInitialValues() });
	closePopOver();
};

// These keys are necessary because of a bug in AppDatePicker that it changes the value internally without waiting for the prop change
// Need to evaluate if making changes to DatePicker is in scope of this mission
const startDatePickerKey = ref(0);
const endDatePickerKey = ref(0);

const onUserSelected = async (id: User['id']) => {
	if (id) {
		booking.selectedEntityId = id;
		booking.selectedEntityType = TYPES.User;
	}
};

const checkAndMoveEndDate = (endDate: Date, startDate: Date) => {
	if (resetTime(endDate) < resetTime(startDate)) {
		booking.endDate = booking.startDate;
	}
};

const onUpdateStartDate = (startDate: SAPIDate) => {
	if (startDate) {
		booking.startDate = startDate;
		checkAndMoveEndDate(resetTime(booking.endDate), resetTime(startDate));
	} else {
		booking.startDate = dateToMysqlString(bookingStartDate.value);
		startDatePickerKey.value++;
	}

	validateInputField('startDate');

	if (!validateStartDate(booking.startDate)) {
		addErrorMessage({
			message: t('resource_planning_start_date_invalid'),
		});
	}
};

const onUpdateEndDate = (endDate: SAPIDate) => {
	if (!endDate) {
		booking.endDate = dateToMysqlString(bookingEndDate.value);
		endDatePickerKey.value++;
	} else {
		booking.endDate = endDate;
		checkAndMoveEndDate(resetTime(endDate), resetTime(booking.startDate));
	}

	validateInputField('endDate');

	if (!validateEndDate(booking.endDate)) {
		addErrorMessage({
			message: t('resource_planning_end_date_invalid'),
		});
	}
};

const unitSelectOptions = [
	{
		id: BookingAmountType.Percent,
		title: t('resource-booking__percent-per-day').toLowerCase(),
	},
	{
		id: BookingAmountType.Hour,
		title: t('resource-booking__hours-per-day').toLowerCase(),
	},
	{
		id: BookingAmountType.Total,
		title: t('resource-booking__hours-total').toLowerCase(),
	},
];

const onUpdateBookingUnit = (unit: BookingAmountType) => {
	if (unit && unit !== booking.unit) {
		booking.unit = unit;

		const isTotalUnitType = unit === BookingAmountType.Total;

		if (isTotalUnitType) {
			const selectedUserId =
				booking.selectedEntityType === TYPES.User &&
				booking.selectedEntityId
					? booking.selectedEntityId
					: '';
			const userAvailability = getUserAvailability(selectedUserId);

			const newBookingAmountTime = shortDuration(
				getRemainingAvailabilityDuration({
					startDate: resetTime(booking.startDate),
					endDate: resetTime(booking.endDate),
					resourceType: booking.selectedEntityType,
					userAvailability,
					includeWeekends: includeWeekends.value,
				}).availability
			)[0].value;

			booking.amountTime = newBookingAmountTime;
		}

		const isHourUnitType = unit === BookingAmountType.Hour;

		if (isHourUnitType) {
			const newBookingAmountTime =
				getFormattedDefaultBookingAmountPerDay();

			booking.amountTime = newBookingAmountTime;
		}
	}
};

const totalBookingDuration = computed(() => {
	const startDate = resetTime(booking.startDate);
	const endDate = resetTime(booking.endDate);
	const selectedUserId =
		booking.selectedEntityType === TYPES.User && booking.selectedEntityId
			? booking.selectedEntityId
			: '';
	const userAvailability = getUserAvailability(selectedUserId);

	const { duration, daysUsed } = getTotalBookingDuration({
		startDate,
		endDate,
		bookingType: booking.unit,
		bookedDuration: timeToSeconds(booking.amountTime),
		bookedPercent: Number(booking.amountPercent),
		userAvailability,
		resourceId: booking.selectedEntityId,
		resourceType: booking.selectedEntityType,
		includeWeekends: includeWeekends.value,
	});

	return {
		duration: formatLongDuration({
			duration,
			removeMinutesOnFullHours: true,
		}),
		daysUsed,
	};
});

const workingDays = computed(() => totalBookingDuration.value.daysUsed);

const perDayBookingDuration = computed(() => {
	if (booking.unit !== BookingAmountType.Total) {
		return;
	}

	let durationPerDay = 0;

	if (workingDays.value > 0) {
		durationPerDay = timeToSeconds(booking.amountTime) / workingDays.value;
	}

	return formatLongDuration({
		duration: durationPerDay,
		removeMinutesOnFullHours: true,
	});
});

const bookingDurationToDisplay = computed(() =>
	booking.unit === BookingAmountType.Total
		? perDayBookingDuration.value
		: totalBookingDuration.value.duration
);

const getAmountTypeForTrackEvent = (unit: BookingAmountType) => {
	if (unit === BookingAmountType.Hour) {
		return 'h per day';
	}

	if (unit === BookingAmountType.Total) {
		return 'h total';
	}

	return '% each day';
};

const { saveBookingMutation, isLoadingSaveBooking } =
	useResourcePlanningUtils();
const { mutate: deleteBooking, loading: loadingDelete } =
	useResourceBookingDeleteMutation({});

const isNewBookingTooEarly = computed(
	() => !booking.bookingId && isStartDateDisabled(booking.startDate)
);

const isSaveButtonDisabled = computed(() => {
	return (
		isLoadingSaveBooking.value ||
		!booking.selectedEntityId ||
		v.value.$errors.length > 0 ||
		isNewBookingTooEarly.value
	);
});

const saveButtonDisabledTooltip = computed(() => {
	if (isNewBookingTooEarly.value) {
		return t('resource_planning_start_date_invalid');
	}

	return undefined;
});

const onResourceSave = async () => {
	const input: BookingSaveInput = {
		id: booking.bookingId,
		projectId: projectId.value,
		amountType: booking.unit,
		userId:
			booking.selectedEntityType === TYPES.User
				? booking.selectedEntityId
				: null,
		roleProjectId:
			booking.selectedEntityType === TYPES.RoleProject
				? booking.selectedEntityId
				: null,
		amount:
			booking.unit === BookingAmountType.Percent
				? parseInt(booking.amountPercent)
				: timeToSeconds(booking.amountTime),
		startDate: booking.startDate,
		endDate: booking.endDate,
		isTentative: booking.isTentative,
		description: booking.description || null,
	};

	const isMovingExistingBooking =
		bookingId.value &&
		booking.selectedEntityType === TYPES.User &&
		booking.selectedEntityId !== selectedEntityId.value;

	if (isMovingExistingBooking) {
		emit(
			'update-booking',
			input,
			selectedEntityId.value,
			selectedEntityType.value
		);

		return;
	}

	const res = await saveBookingMutation(input);

	if (res?.errors) {
		return;
	}

	if (!bookingId.value) {
		track('Booking Created', {
			module: props.showProject
				? Module.BOOKINGS.name
				: Module.PROJECTS.name,
			booking_type: selectedAllocationType.value,
			amount_type: getAmountTypeForTrackEvent(booking.unit),
			booking_assignee:
				booking.selectedEntityType === TYPES.RoleProject
					? 'placeholder'
					: 'individual',
		});
	}

	booking.bookingId = res?.data?.BookingSave.id ?? '';

	modalClose();
};

const onDeleteBooking = async () => {
	const response = await deleteBooking({
		input: {
			id: props.bookingId,
		},
	});

	if (response?.errors) {
		addErrorMessage({
			message: t('resource-booking__delete-failed'),
		});

		return;
	}
	modalClose();
};

onMounted(() => {
	if (booking.selectedEntityId && booking.selectedEntityType === TYPES.User) {
		loadAvailability(
			resetTime(booking.startDate),
			resetTime(booking.endDate),
			[booking.selectedEntityId]
		);
	}

	const background = document.createElement('div');

	background.className = 'resource-booking-modal__background';
	background.addEventListener('click', modalClose);

	document.body.appendChild(background);
});

onUnmounted(() => {
	const background = document.querySelector(
		'.resource-booking-modal__background'
	);
	background?.remove();
});
</script>

<style lang="scss" scoped>
.resource-booking-modal {
	--sui-modal-dialog-max-width: max-content;

	&__form {
		display: flex;
		gap: 2rem;
	}

	&__form-content {
		display: flex;
		flex-direction: column;
		gap: 2.5rem;
		width: 100%;
	}

	&__project {
		font-size: 1.625rem;
		color: $neutral-90;
		display: flex;
		align-items: center;
		gap: 0.5rem;
		height: 4rem;
	}

	&__inputs-wrapper {
		display: flex;
		align-items: center;
		gap: 2rem;
	}

	&__inputs-column {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}

	&__date-select-wrapper,
	&__amount-select-wrapper {
		display: flex;
		border: 1px solid $neutral-40;
		border-radius: 0.5rem;

		:deep(.sui-input) {
			border-style: none;
		}

		:deep(.sui-input__placeholder-label) {
			max-width: none;
		}
	}

	&__start-date-input,
	&__end-date-input {
		width: 14rem;
		border-style: none;
	}

	&__start-date-input {
		border-right: 1px solid $neutral-40;
	}

	&__amount-type {
		width: 15rem;
		border-left: 1px solid $neutral-40;
	}

	&__amount-input {
		width: 12rem;
	}

	&__controls-wrapper {
		padding: 2rem 3rem;
		background-color: $neutral-10;
		display: flex;
		justify-content: flex-end;
	}

	&__button-wrapper {
		display: flex;
		gap: 1rem;
	}

	&__working-days {
		font-size: 1.375rem;
		color: $neutral-70;
	}

	&__total {
		font-size: 1.375rem;
		color: $neutral-70;
	}

	&__total-text,
	&__working-days-text {
		font-weight: $scoro-fw-semi-bold;
		color: $neutral-90;
	}

	&__delete-button {
		margin-right: auto;
		padding: 1rem;

		--sui-color-link: #{$neutral-60};
	}

	&__description {
		width: 100%;
	}
}
</style>

<style lang="scss">
.resource-booking-modal__background {
	z-index: z('modal-popup');
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	position: fixed;
}
</style>
